import { NavLink } from 'react-router';
import { token } from '@bp-files/ui';
import { APP_COPY } from '@/copy/app.copy';
import { RECYCLE_BIN_COPY } from '@/components/RecycleBin/recycleBin.copy';
import { useFolderDestinations } from '@/lib/useFolderDestinations';

const SURFACE_LINK_CLASS =
  'flex w-full flex-col items-start rounded-r-md px-3 py-2 text-sm font-medium leading-tight transition-colors';

function surfaceLinkStyle({ isActive }: { isActive: boolean }) {
  return {
    color: isActive ? token('bp-blue-text') : token('bp-text-muted'),
    borderLeftWidth: token('rail-indicator'),
    borderLeftStyle: 'solid' as const,
    borderLeftColor: isActive ? token('bp-blue-rail') : 'transparent',
  };
}

/**
 * Every accessible destination is a stable, directly clickable entry (FIL-1d).
 *
 * **One rail for both roles** (FIL-1e). It used to title itself "Shared Spaces"
 * for an admin, because an admin's `/files` was a grid of spaces and a member's
 * was this list. There is one surface now, so there is one word for it.
 */
export function FilesNav() {
  const { state } = useFolderDestinations();
  const folders = state.kind === 'loaded' ? state.folders : [];

  return (
    <div className="flex h-full min-h-0 flex-col gap-1 overflow-hidden">
      <NavLink
        to="/files"
        className="mb-3 shrink-0 truncate rounded-r-md px-3 pt-1.5 pb-2 text-h2 font-semibold leading-none tracking-tight"
        style={{ color: token('bp-text') }}
      >
        {APP_COPY.nav.folders}
      </NavLink>

      <div className="min-h-0 flex-1">
        {state.kind === 'loading' ? (
          <FolderSkeleton />
        ) : folders.length > 0 ? (
          <ul
            aria-label={APP_COPY.nav.folders}
            className="flex h-full min-h-0 flex-col gap-1 overflow-y-auto"
          >
            {folders.map((folder) => (
              <li key={`${folder.spaceId}/${folder.folderId}`}>
                <NavLink
                  to={`/files/spaces/${encodeURIComponent(folder.spaceId)}/folders/${encodeURIComponent(folder.folderId)}`}
                  aria-label={folder.displayName}
                  className={SURFACE_LINK_CLASS}
                  style={surfaceLinkStyle}
                >
                  <span className="w-full truncate font-medium">{folder.spaceName}</span>
                  {/*
                    `isRoot`, not a comparison of the two names: a root carries
                    its space's name, and so does a folder somebody named after
                    the room (FIL-1e). The second must keep its subtitle.
                  */}
                  {folder.isRoot ? null : (
                    <span
                      className="mt-0.5 w-full truncate text-xs font-normal"
                      style={{ color: token('bp-text-muted') }}
                    >
                      {folder.folderName}
                    </span>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <NavLink
        to="/files/recycle-bin"
        className={`${SURFACE_LINK_CLASS} shrink-0`}
        style={surfaceLinkStyle}
      >
        {RECYCLE_BIN_COPY.entry.label}
      </NavLink>
    </div>
  );
}

function FolderSkeleton() {
  return (
    <div role="status" className="flex flex-col gap-2 px-3 py-1.5">
      <span className="sr-only">{APP_COPY.nav.foldersLoading}</span>
      {[0, 1].map((row) => (
        <span key={row} aria-hidden="true" className="h-2.5 animate-pulse rounded-sm bg-elevated" />
      ))}
    </div>
  );
}
